import React from 'react';
import { useLocation } from 'react-router';
import { useAtom, useAtomValue } from 'jotai';
import { Strip } from '../../../Models/Article';
import { ShowStripStatus } from '../../../Models/Dashboard/DashNewsItem';
import { UpcomingReleasesAtom } from '../../../store/DiscoverStore';
import { FetchReadingProgressAtom } from '../../../store/ReadingProgressStore';
import { AccountInfoAtom, LoginStatusAtom } from '../../../store/AccountStore';
import InkyErrorBoundary from '../InkyErrorBoundary';
import scrollIntoView from './scrollIntoView';
import './ArticleContent.scss';
import useStyle from './useStyle';

let defaultSpacerHeight: number;
/////////////////////////////////
defaultSpacerHeight = 70;
/////////
/////////////////////////
//////////


interface ArticleContentProps {
    id: number;
    style?: string;
    strips: Strip[];
    renderStrip: (strip: Strip) => React.ReactElement | null;
}

export function ArticleContent({ id, style = '', strips, renderStrip }: ArticleContentProps) {
    useHashLinkScroll(id);
    let Component: React.ComponentType<ArticleContentProps> = Content;
    if (strips.some(strip => strip.container === 'upcoming-releases-wheel')) {
        Component = withUpcomingReleases(Component);
    }
    if (strips.some(strip => strip.container === 'continue-reading-grid')) {
        Component = withContinueReading(Component);
    }
    return <Component key={id} id={id} style={style} strips={strips} renderStrip={renderStrip} />;
}

function useHashLinkScroll(id: number) {
    const { hash } = useLocation();
    const lastRenderedId = React.useRef(-1);
    React.useEffect(() => {
        if (lastRenderedId.current === id) {
            return;
        }
        lastRenderedId.current = id;
        if (hash === '') {
            return;
        }
        window.requestAnimationFrame(() => {
            const scrollTarget = document.querySelector(hash);
            if (!scrollTarget) {
                return;
            }
            scrollIntoView(scrollTarget);
        });
    }, [id, hash]);
}

function withUpcomingReleases(Component: React.ComponentType<ArticleContentProps>) {
    return function ArticleContentWithUpcomingReleases({ id, style, strips, renderStrip }: ArticleContentProps) {
        const items = useAtomValue(UpcomingReleasesAtom);
        return (
            <Component
                id={id}
                style={style}
                strips={items.length === 0
                    ? strips.filter(strip => strip.container !== 'upcoming-releases-wheel')
                    : strips}
                renderStrip={renderStrip}
            />
        );
    };
}

function withContinueReading(Component: React.ComponentType<ArticleContentProps>) {
    return function ArticleContentWithContinueReading({ id, style, strips, renderStrip }: ArticleContentProps) {
        const [items, fetchItems] = useAtom(FetchReadingProgressAtom);
        React.useEffect(() => {
            (async () => {
                await fetchItems();
            })();
        }, [fetchItems]);
        return (
            <Component
                id={id}
                style={style}
                strips={items.length === 0
                    ? strips.filter(strip => strip.container !== 'continue-reading-grid')
                    : strips}
                renderStrip={renderStrip}
            />
        );
    };
}

function Content({ id, style, strips, renderStrip }: ArticleContentProps) {
    const loginStatus = useAtomValue(LoginStatusAtom);
    const accountInfo = useAtomValue(AccountInfoAtom);

    const stripsWithSpacers = React.useMemo(() => {
        const visibleStrips = strips.filter(strip => {
            if (
                strip.showStripStatus === ShowStripStatus.LoggedOut &&
                loginStatus === 'LoggedIn'
            ) {
                return false;
            }
            if (
                strip.showStripStatus === ShowStripStatus.LoggedIn &&
                loginStatus !== 'LoggedIn'
            ) {
                return false;
            }
            if (
                strip.showStripStatus === ShowStripStatus.LoggedInVerified &&
                (loginStatus !== 'LoggedIn' || !accountInfo.verified)
            ) {
                return false;
            }
            if (
                strip.showStripStatus === ShowStripStatus.LoggedInUnverified &&
                (loginStatus !== 'LoggedIn' || accountInfo.verified)
            ) {
                return false;
            }
            if (
                strip.showStripStatus === ShowStripStatus.LoggedInSubscribed &&
                (loginStatus !== 'LoggedIn' || !accountInfo.subscription.isActive)
            ) {
                return false;
            }
            if (
                strip.showStripStatus === ShowStripStatus.LoggedInNotSubscribed &&
                (loginStatus !== 'LoggedIn' || accountInfo.subscription.isActive)
            ) {
                return false;
            }
            return !('params' in strip && 'items' in strip.params && (strip.params.items as unknown[]).length === 0);
        });
        const visibleStripsWithSpacers: Strip[] = [];
        for (let i = 0; i < visibleStrips.length; i++) {
            // add a spacer between two non-spacer strips
            const thisStrip = visibleStrips[i];
            const previousStrip = visibleStrips[i - 1];
            if (i > 0 && thisStrip.container !== 'spacer' && previousStrip.container !== 'spacer') {
                visibleStripsWithSpacers.push(Object.assign(new Strip(), {
                    __reactKey: `local-${previousStrip.__reactKey}-spacer-${thisStrip.__reactKey}`,
                    container: 'spacer',
                    params: { height: defaultSpacerHeight },
                }));
            }
            visibleStripsWithSpacers.push(visibleStrips[i]);
        }
        return visibleStripsWithSpacers;
    }, [strips, loginStatus, accountInfo]);

    useStyle({ id: `article-${id}`, style });

    return (
        <div className='ArticleContent'>
            {stripsWithSpacers.map(strip => (
                <InkyErrorBoundary
                    key={strip.__reactKey}
                    customErrorHeader={`Error rendering strip ${strip.container}`}
                    extraInfo={JSON.stringify(strip, null, 2)}
                >
                    <ArticleContentStrip key={strip.__reactKey} strip={strip} renderStrip={renderStrip} />
                </InkyErrorBoundary>
            ))}
        </div>
    );
}

interface ArticleContentStripProps {
    strip: Strip,
    renderStrip: (strip: Strip) => React.ReactElement | null;
}

function ArticleContentStrip({ strip, renderStrip }: ArticleContentStripProps) {
    const classNames = ['ArticleStrip'];
    if (strip.cssClass) {
        classNames.push(strip.cssClass);
    }
    const content = renderStrip(strip);
    return <div id={strip.htmlAnchor} className={classNames.join(' ')}>{content}</div>;
}
